import { Image as ImageIcon, Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { handleImageUpload } from "./admin-utils";

export function MenuItemModal({
  open,
  onOpenChange,
  editingItem,
  menuForm,
  setMenuForm,
  categories,
  onSave,
  saving,
}) {
  const isRotatingCategory = categories.find((c) => c.key === menuForm.category)?.is_rotating;

  const addVariant = () => {
    setMenuForm((prev) => ({ ...prev, variants: [...prev.variants, { name: "", price: "" }] }));
  };

  const updateVariant = (index, field, value) => {
    setMenuForm((prev) => {
      const variants = [...prev.variants];
      variants[index] = { ...variants[index], [field]: value };
      return { ...prev, variants };
    });
  };

  const removeVariant = (index) => {
    setMenuForm((prev) => ({ ...prev, variants: prev.variants.filter((_, i) => i !== index) }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>{editingItem ? "Edit Menu Item" : "Add Menu Item"}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-4">
            {/* Image Upload */}
            <div>
              <Label>Image</Label>
              <div
                className="mt-2 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-[#1aa59a] transition-colors"
                onClick={() => document.getElementById("menu-image-input").click()}
              >
                {menuForm.image_base64 ? (
                  <img src={menuForm.image_base64} alt="Preview" className="max-h-24 mx-auto rounded" />
                ) : (
                  <div className="text-gray-400">
                    <ImageIcon className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Click to upload image</p>
                  </div>
                )}
              </div>
              <input
                id="menu-image-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleImageUpload(e, (img) => setMenuForm({ ...menuForm, image_base64: img }))}
              />
              {menuForm.image_base64 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 text-red-500"
                  onClick={() => setMenuForm({ ...menuForm, image_base64: null })}
                >
                  Remove Image
                </Button>
              )}
            </div>

            <div>
              <Label htmlFor="menu-name">Name *</Label>
              <Input
                id="menu-name"
                value={menuForm.name}
                onChange={(e) => setMenuForm({ ...menuForm, name: e.target.value })}
                placeholder="Dish name"
              />
            </div>

            <div>
              <Label htmlFor="menu-desc">Description</Label>
              <Textarea
                id="menu-desc"
                value={menuForm.description}
                onChange={(e) => setMenuForm({ ...menuForm, description: e.target.value })}
                placeholder="Describe the dish"
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="menu-price">Base Price (£) *</Label>
              <Input
                id="menu-price"
                type="number"
                step="0.01"
                value={menuForm.price}
                onChange={(e) => setMenuForm({ ...menuForm, price: e.target.value })}
                placeholder="0.00"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Variants (optional)</Label>
                <Button variant="outline" size="sm" onClick={addVariant}>
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
              {menuForm.variants.map((variant, idx) => (
                <div key={variant.name || `variant-${idx}`} className="flex gap-2 mb-2">
                  <Input
                    placeholder="Name"
                    value={variant.name}
                    onChange={(e) => updateVariant(idx, "name", e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    placeholder="£"
                    type="number"
                    step="0.01"
                    value={variant.price}
                    onChange={(e) => updateVariant(idx, "price", e.target.value)}
                    className="w-20"
                  />
                  <Button variant="ghost" size="icon" className="text-red-500" onClick={() => removeVariant(idx)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div>
              <Label>Category *</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {categories.map((cat) => (
                  <button
                    key={cat.key}
                    type="button"
                    onClick={() =>
                      setMenuForm({
                        ...menuForm,
                        category: cat.key,
                        week_number: cat.is_rotating ? menuForm.week_number || 1 : null,
                      })
                    }
                    className={`px-3 py-1 rounded-full text-sm transition-all ${
                      menuForm.category === cat.key ? "text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    style={menuForm.category === cat.key ? { backgroundColor: cat.color } : {}}
                  >
                    {cat.label}
                    {cat.is_rotating && <span className="ml-1 text-xs opacity-75">🔄</span>}
                  </button>
                ))}
              </div>
            </div>

            {isRotatingCategory && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <Label className="text-blue-700">Assign to Week *</Label>
                <p className="text-xs text-blue-600 mb-2">
                  This category rotates weekly. Select which week this item appears.
                </p>
                <div className="flex gap-2">
                  {[1, 2, 3].map((week) => (
                    <button
                      key={week}
                      type="button"
                      onClick={() => setMenuForm({ ...menuForm, week_number: week })}
                      className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                        menuForm.week_number === week
                          ? "bg-[#1aa59a] text-white"
                          : "bg-white border border-gray-200 text-gray-600 hover:border-[#1aa59a]"
                      }`}
                    >
                      Week {week}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <Label htmlFor="menu-active">Show on Menu</Label>
              <Switch
                id="menu-active"
                checked={menuForm.is_active}
                onCheckedChange={(checked) => setMenuForm({ ...menuForm, is_active: checked })}
              />
            </div>

            <div>
              <Label>Delivery Schedule</Label>
              <p className="text-[11px] text-gray-500 mb-2">
                Which days can we deliver this item? Determines how it groups on the customer basket.
              </p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: "all_week", label: "All Week", sub: "Mon-Sat" },
                  { value: "mon_wed", label: "Mon-Wed", sub: "Made early" },
                  { value: "wed_fri", label: "Wed-Fri", sub: "Made mid-week" },
                ].map((opt) => {
                  const active = (menuForm.delivery_schedule || "all_week") === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setMenuForm({ ...menuForm, delivery_schedule: opt.value })}
                      className={`rounded-lg border-2 py-2 px-1 text-center transition-all ${
                        active
                          ? "border-[#1aa59a] bg-[#1aa59a]/5"
                          : "border-gray-200 bg-white hover:border-[#1aa59a]/50"
                      }`}
                      data-testid={`menu-schedule-${opt.value}`}
                    >
                      <div className={`text-xs font-semibold ${active ? "text-[#1aa59a]" : "text-gray-700"}`}>
                        {opt.label}
                      </div>
                      <div className="text-[10px] text-gray-400">{opt.sub}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving} className="bg-[#1aa59a] hover:bg-[#158a80]">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingItem ? "Save" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
